import { writable, get, type Writable } from 'svelte/store';
import { mutate } from '$lib/apollo';

export function diff(a: any, b: any) {
  const aKeys = Object.keys(a);
  const result = aKeys.reduce((acc, key) => {
    // Basic equality check on the properties
    if (a[key] !== b[key]) {
      if (a[key] && a[key].constructor === Object && b[key] && b[key].constructor === Object) {
        // Recursively call the diff function if both keys in the objects are objects themselves
        const difference = diff(a[key], b[key]);
        if (Object.keys(difference).length > 0) {
          acc[key] = b[key];
        }
      } else if (Array.isArray(a[key]) && Array.isArray(b[key])) {
        // If the first elements in both arrays are objects, recursively call the diff for each element in the array
        if (
          a[key].length > 0 &&
          a[key][0].constructor === Object &&
          b[key].length > 0 &&
          b[key][0].constructor === Object
        ) {
          const difference = diff(a[key], b[key]);
          if (Object.keys(difference).length > 0) {
            acc[key] = b[key];
          }
        }
        // Otherwise, check that every element in an array is the same
        else if (a[key].length !== b[key].length || !b[key].every((v, i) => a[key][i] && a[key][i] === v)) {
          acc[key] = b[key];
        }
      } else if (key in b) {
        acc[key] = b[key];
      }
    }
    return acc;
  }, {});

  // Add any new keys as well
  const difference = Object.keys(b).filter(x => !aKeys.includes(x));
  difference.forEach(key => {
    if (key === '__dirty__') return;
    result[key] = b[key];
  });

  return result;
}

export type DirtyStore<T> = Writable<T & { __dirty__: T }> & {
  /** The data considered dirty. */
  dirty: () => T;
  /** Whether or not the store is dirty. */
  isDirty: () => boolean;
  /** Force key to be considered dirty. */
  setDirty: (key: keyof T) => void;
  /** Saves the current data as the internal tracked data. Future dirty checks
   * will compare against this new data instead of the previous data. */
  makeClean: () => void;
  /** Return the keys of the object passed in, minus the dirty tracking keys. */
  keys: (ref: T) => string[];
  /** Return the values of the object passed in, minus the values of the dirty
   * tracking keys. */
  values: (ref: T) => any[];
  /** Return the current data, minus the dirty tracking keys. */
  current: (ref: T) => T;
  /** Return the entries of the object passed in, minus the dirty tracking keys. */
  length: () => number;
  /** Saves the current data as the internal tracked data. Future dirty checks
   * will compare against this new data instead of the previous data. */
  save: () => void;
  /** Make the store's initial state match what we're about to send to the backend,
   * then mutate the store with the provided params. This should stop the store from
   * being dirty and causing unnecessary re-renders.
   */
  mutate: (params: any) => Promise<{ status: string; recurse: boolean; [key: string]: any }>;
  /** Make the store's initial state match what we're about to send to the backend,
   * then mutate the store with the provided params. This should stop the store from
   * being dirty and causing unnecessary re-renders.
   */
  multiMutate: (promises: Promise<any>[]) => Promise<{ results: any[]; recurse: boolean; status: string }>;
  /** Update the store without creating dirty data. */
  hardUpdate: (fn: (data: T) => T) => void;
  /** Update the store with the provided function. */
  update: (fn: (data: T) => T) => void;
};

export function createDirtyTrackingStore(initialValue: any = {}): DirtyStore<any> {
  // Create the writable store with an internal copy of the initial data.
  const { subscribe, update } = writable({ ...initialValue, __dirty__: {} });

  // Track initial data separately to avoid triggering Svelte reactivity when it changes
  const initial = writable(initialValue);

  const store = {
    subscribe,
    set: value => store.update(v => ({ ...v, ...value })),
    update: fn => {
      // Get the current data
      const data = get(store);
      // Call the provided update func
      const newData = fn(data);
      // Determine the new dirty properties against the initial state and update the store
      const dirty = diff(get(initial), newData);
      // Call the store's actual update with the new data and the resulting dirty data
      update(() => ({ ...newData, __dirty__: { ...dirty } }));
    },
    hardUpdate: fn => {
      // Get the current data
      const data = get(store);
      // Call the provided update func
      const newData = fn(data);
      // Call the store's actual update with the new data and avoid creating dirty data
      update(() => ({ ...newData }));
    },
    mutate: async params => {
      const init = get(initial);
      const dirty = get(store).__dirty__;
      let recurse = false;

      // Update initial data to match what we're about to send to the backend
      initial.update(v => ({ ...v, ...dirty }));

      // Send off mutation to backend
      const { data } = await mutate(params);
      const result: any = Object.values(data)[0];
      const status = result.status === 'success' || result.result ? 'success' : 'error';

      if (status === 'success') {
        // Check if something changed during the mutation so we know whether or not to run the parent function again
        const dirtyDiff = diff(dirty, get(store).__dirty__);
        recurse = Object.keys(dirtyDiff).length > 0;
      } else {
        // Something went wrong, let's revert the initial data
        initial.update(v => ({ ...v, ...init }));
      }

      return { ...result, status, recurse };
    },
    multiMutate: async promises => {
      const init = get(initial);
      const dirty = get(store).__dirty__;
      let recurse = false;

      // Update initial data to match what we're about to send to the backend
      initial.update(v => ({ ...v, ...dirty }));

      // Send off mutations to backend
      const resolvedPromises = await Promise.allSettled(promises);
      const results: any[] = resolvedPromises.map(
        ({ value }: PromiseFulfilledResult<any>) => Object.values(value ? value.data : {})[0]
      );

      // Set status
      const rejectedPromise = resolvedPromises.find(({ status }) => status === 'rejected');
      const failedMutation = results.find(res => {
        // legacy api returns string status
        // new api returns an errors array
        return res ? res.status !== 'success' || !res.result : false;
      });

      // If no rejectedPromise or failedMutation is found, status is success
      const status = rejectedPromise?.status ?? failedMutation?.status ?? 'success';

      if (status === 'success') {
        // Check if something changed during the mutation so we know whether or not to run the parent function again
        const dirtyDiff = diff(dirty, get(store).__dirty__);
        recurse = Object.keys(dirtyDiff).length > 0;
      } else {
        // Something went wrong, let's revert the initial data
        initial.update(v => ({ ...v, ...init }));
      }

      return { results, recurse, status };
    },
    dirty: () => get(store).__dirty__,
    isDirty: () => Object.keys(get(store).__dirty__).length > 0,
    setDirty: key => {
      const data = get(store);
      const val = data[key];
      update(() => ({ ...data, __dirty__: { ...data.__dirty__, [key]: val } }));
    },
    makeClean: () => {
      const data = get(store);
      update(() => ({ ...data, __dirty__: {} }));
    },
    save: () => {
      update(v => ({ ...v, __dirty__: {} }));
      const copy = { ...get(store) };
      delete copy.__dirty__;
      initial.set(copy);
    },
    keys: ref => {
      const copy = { ...ref };
      delete copy.__dirty__;
      return Object.keys(copy);
    },
    values: ref => {
      const copy = { ...ref };
      delete copy.__dirty__;
      return Object.values(copy);
    },
    current: ref => {
      const copy = { ...ref };
      delete copy.__dirty__;
      return copy;
    },
    length: () => Object.keys(get(store)).length - 1
  };
  return store;
}
